import type { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { useMemo } from 'react';
import { GET_AI_SEARCH_ENABLED } from './queries';
import { SET_AI_SEARCH_ENABLED } from './mutations';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';

/**
 * The AI search preference is not society-scoped, but the GraphQL proxy
 * still requires X-Society-Id. Fall back to "1" when none is selected.
 */
function usePreferenceHeaders() {
  const orgId = usePublicSearchContext((ctx) => ctx.society.orgId);
  return useMemo(() => ({ headers: setDefaultHeader(orgId || '1') }), [orgId]);
}

export function useAiSearchEnabled(skip = false) {
  const requestContext = usePreferenceHeaders();
  const { data, loading, refetch } = useQuery(
    GET_AI_SEARCH_ENABLED as TypedDocumentNode<{ aiSearchEnabled: boolean }>,
    {
      skip,
      fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
      context: requestContext,
    },
  );

  return {
    aiSearchEnabled: data?.aiSearchEnabled ?? false,
    loading,
    refetch,
  };
}

export function useSetAiSearchEnabled() {
  const requestContext = usePreferenceHeaders();

  return useMutation(
    SET_AI_SEARCH_ENABLED as TypedDocumentNode<
      { setAiSearchEnabled: boolean },
      { enabled: boolean }
    >,
    {
      context: requestContext,
      update(cache, { data }) {
        if (data?.setAiSearchEnabled === undefined) return;
        cache.writeQuery({
          query: GET_AI_SEARCH_ENABLED,
          data: { aiSearchEnabled: data.setAiSearchEnabled },
        });
      },
    },
  );
}
